import {
  DatalayerService,
  provideDatalayerOutputs,
  provideDatalayerSideEffects,
} from '@analytics/datalayer';
import {
  ANALYTICS_ENHANCERS,
  AnalyticsEnhancerSources,
  provideEnhancers,
} from '@analytics/enhancers';
import { NavigationEventsService } from '@analytics/events';
import type {
  AnalyticsEnhancer,
  DatalayerOutput,
  DatalayerStreamSideEffect,
} from '@analytics/models';
import { DOCUMENT } from '@angular/common';
import type { Type } from '@angular/core';
import { APP_INITIALIZER, makeEnvironmentProviders } from '@angular/core';
import { PlatformService } from '@core/platform';

type AnalyticsProviderArgs = {
  enhancers: Type<AnalyticsEnhancer>[];
  sideEffects: Type<DatalayerStreamSideEffect>[];
  outputs: Type<DatalayerOutput>[];
};

/**
 * Sets up the analytics providers for the application environment.
 * This function simplifies the registration of analytics features like enhancers,
 * side effects, and outputs, and sets up an APP_INITIALIZER to configure
 * the analytics services when the application starts.
 *
 * @param args - The configuration object for analytics providers.
 * @param args.enhancers - An array of AnalyticsEnhancer classes to be registered.
 * @param args.sideEffects - An array of DatalayerStreamSideEffect classes to be registered.
 * @param args.outputs - An array of DatalayerOutput classes to be registered.
 * @returns An Angular EnvironmentProviders object for use in the application's bootstrap configuration.
 */
export function provideAnalytics({
  enhancers,
  sideEffects,
  outputs,
}: AnalyticsProviderArgs) {
  return makeEnvironmentProviders([
    ...enhancers,
    ...sideEffects,
    ...outputs,
    provideEnhancers(enhancers),
    provideDatalayerSideEffects(sideEffects),
    provideDatalayerOutputs(outputs),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: analyticsInitializerFactory,
      deps: [
        AnalyticsEnhancerSources,
        ANALYTICS_ENHANCERS,
        NavigationEventsService,
        DatalayerService,
        PlatformService,
        DOCUMENT,
      ],
    },
  ]);
}

/**
 * Factory function for the analytics APP_INITIALIZER.
 * This function is executed when the Angular application is initialized. It sets up
 * the core analytics functionality by adding enhancers, starting navigation tracking,
 * initializing the datalayer, and firing an event to signal that Angular has loaded.
 * @internal
 *
 * @param sources - The service to which analytics enhancers are added.
 * @param enhancers - The array of instantiated AnalyticsEnhancer services.
 * @param navigationEvents - The service that tracks user navigation for analytics.
 * @param datalayer - The core datalayer service that needs initialization.
 * @param platform - A service to determine if the code is running on the client or server.
 * @param document - reference to the document token
 * @returns An asynchronous function that performs the initialization.
 */
function analyticsInitializerFactory(
  sources: AnalyticsEnhancerSources,
  enhancers: AnalyticsEnhancer[],
  // old deps
  navigationEvents: NavigationEventsService,
  datalayer: DatalayerService,
  platform: PlatformService,
  document: Document
) {
  return async function initializeAnalytics() {
    enhancers.forEach((instance) => sources.addEnhancers(instance));

    navigationEvents.trackNavigationEvents();
    datalayer.init();

    if (platform.isClient()) {
      document.body.dispatchEvent(new CustomEvent('AngularLoaded'));
    }
  };
}
